import { useCallback } from 'react';
import {
  useWithdrawCrypto,
  useWithdrawCryptoModals,
  type WithdrawCryptoModalState,
  type WithdrawCryptoRequestParams,
} from '@/features/withdraw-crypto';
import { useTranslation } from 'react-i18next';
import type {
  TwoFAParams,
  WithdrawCommissionData,
  WithdrawCryptoErrorResponse,
  WithdrawSuccessResponse,
} from '@/shared/types';
import type { InfoDataGridData } from '@/shared/components';
import { WithdrawConfirmationModal } from '@/shared/components';
import { useWithdrawMFAFlow } from '@/shared/lib';
import { useAfterOperationRefresh } from '@/entities/operation';

interface WithdrawConfirmationModalContainerProps {
  onBack: () => void;
  storeData: WithdrawCryptoModalState;
  commissionData: WithdrawCommissionData;
  continueMFA?: boolean;
}

export const WithdrawConfirmationModalContainer = ({
  onBack,
  storeData,
  commissionData,
  continueMFA,
}: WithdrawConfirmationModalContainerProps) => {
  const { t } = useTranslation();
  const { openResultModal, openConfirmationModal } = useWithdrawCryptoModals();

  const afterOperationRefresh = useAfterOperationRefresh();
  const initialParams: WithdrawCryptoRequestParams = {
    networkName: storeData.selectedNetwork.name,
    body: {
      accountNumber: storeData.accountNumber,
      amount: storeData.totalAmount.toString(),
      assetCode: storeData.withdrawCryptoFormData.assetCode,
      networkCode: storeData.selectedNetwork.code,
      cryptoAddress: storeData.withdrawCryptoFormData.cryptoAddress,
    },
  };

  const injectMFA = useCallback(
    (
      params: WithdrawCryptoRequestParams,
      mfa: TwoFAParams
    ): WithdrawCryptoRequestParams => {
      return {
        ...params,
        body: {
          ...params.body,
          twoFA: mfa,
        },
      };
    },
    []
  );

  const { showMFA, startMFAVerification, renderMFAView } = useWithdrawMFAFlow<
    WithdrawCryptoRequestParams,
    WithdrawSuccessResponse,
    WithdrawCryptoErrorResponse
  >({
    initialParams,
    injectMFA,
    useMutationHook: useWithdrawCrypto,
    onSuccess: () => {
      openResultModal({ success: true });
      afterOperationRefresh();
    },
    onError: () => {
      openResultModal({ success: false });
    },
    onBack: openConfirmationModal,
    onClose: openConfirmationModal,
    onCancel: openConfirmationModal,
    onMFASetupComplete: () => {
      openConfirmationModal({ continueMFA: true });
    },
    modalTitle: 'withdrawMethods.crypto.confirmModal.title',
    continueMFA,
  });

  const onConfirmed = useCallback(() => {
    startMFAVerification();
  }, [startMFAVerification]);

  const addressInfo: InfoDataGridData = storeData
    ? [
        {
          key: t(
            'withdrawMethods.crypto.confirmModal.addressInfo.withdrawAsset'
          ),
          value: `${storeData?.withdrawCryptoFormData.assetCode} (${storeData?.selectedAsset.name})`,
        },
        {
          key: t('withdrawMethods.crypto.confirmModal.addressInfo.network'),
          value: `${storeData?.selectedNetwork.name} ${storeData?.selectedNetwork.code}`,
        },
        {
          key: t('withdrawMethods.crypto.confirmModal.addressInfo.address'),
          value: storeData?.withdrawCryptoFormData.cryptoAddress,
        },
      ]
    : [];

  const amountsInfo: InfoDataGridData =
    storeData && commissionData
      ? [
          {
            key: t('withdrawMethods.amountInfo.totalWithdraw'),
            value: `${commissionData.totalWithdrawAmountStr}`,
          },
          {
            key: t('withdrawMethods.amountInfo.withdrawFee'),
            value: `${commissionData.feeStr}`,
          },
          {
            key: t('withdrawMethods.amountInfo.toBeReceived'),
            value: `${commissionData.toBeReceivedStr}`,
          },
        ]
      : [];

  if (showMFA) {
    return renderMFAView();
  }

  return (
    <WithdrawConfirmationModal
      title={t('withdrawMethods.crypto.confirmModal.title')}
      onBack={onBack}
      infoData={[addressInfo, amountsInfo]}
      onConfirmed={onConfirmed}
      isLoading={false}
      buttonText={t('withdrawMethods.crypto.confirmModal.submitButton')}
      footerWarning={t(
        'withdrawMethods.crypto.confirmModal.footerWarningMessage'
      )}
    />
  );
};
